import { useMemo, useState } from 'react';
import type { ColumnDef, SortingState, PaginationState } from '@tanstack/react-table';
import { DataTable } from '@/components/data-display/DataTable';
import type { RevenueDetail } from './types';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: number): string {
  return (
    '$' +
    value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface RevenueBreakdownProps {
  details: RevenueDetail[];
  isLoading: boolean;
}

const PAGE_SIZE = 20;

export function RevenueBreakdown({ details, isLoading }: RevenueBreakdownProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'total_client_revenue', desc: true },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: PAGE_SIZE,
  });

  const columns = useMemo<ColumnDef<RevenueDetail, unknown>[]>(
    () => [
      {
        accessorKey: 'client_name',
        header: 'Client',
        enableSorting: true,
        cell: ({ row }) => (
          <span
            className="inline-block max-w-[280px] truncate font-semibold text-[#333]"
            title={row.original.client_name}
            data-testid={`client-name-${row.original.client_id}`}
          >
            {row.original.client_name}
          </span>
        ),
      },
      {
        accessorKey: 'transfer_fees',
        header: 'Wire Fees',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="text-[13px] text-[#333]">
            {formatCurrency(row.original.transfer_fees)}
          </span>
        ),
      },
      {
        accessorKey: 'fx_revenue',
        header: 'FX Revenue',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="text-[13px] text-[#333]">{formatCurrency(row.original.fx_revenue)}</span>
        ),
      },
      {
        accessorKey: 'total_client_revenue',
        header: 'Total Revenue',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="text-[13px] font-semibold text-[#1a1a1a]">
            {formatCurrency(row.original.total_client_revenue)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div data-testid="revenue-breakdown">
      <h2 className="mb-4 text-lg font-bold text-[#1a1a1a]">Per-Client Breakdown</h2>
      <DataTable<RevenueDetail>
        columns={columns}
        data={details}
        sorting={sorting}
        onSortingChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        isLoading={isLoading}
      />
    </div>
  );
}
